import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { IRichLogData, IRichLogRecord, RichLogLevel } from "@ryde/shared";

import { IRichLogsFilter, RichLogFormFields } from "../rich-logs.models";
import {
  filterRecordsExcludingLevels,
  getNormalizedRichLogLevel,
  sortRichLogs,
} from "../rich-logs.utils";

interface IUseRichLogsFilteredRecordsProps {
  logData: IRichLogData | undefined;
}

export const useRichLogsFilteredRecords = ({
  logData,
}: IUseRichLogsFilteredRecordsProps): IRichLogRecord[] => {
  const { watch } = useFormContext<IRichLogsFilter>();
  const { t } = useTranslation();

  const searchText = watch(RichLogFormFields.searchText);
  const levels = watch(RichLogFormFields.levels);
  const group = watch(RichLogFormFields.group);
  const commaMode = watch(RichLogFormFields.commaMode);
  const step = watch(RichLogFormFields.step);
  const sort = watch(RichLogFormFields.sort);
  const showDebugInfo = watch(RichLogFormFields.showDebugInfo) || false;
  const withAiHintsOnly = watch(RichLogFormFields.withAiHintsOnly) || false;

  return useMemo(() => {
    let result = filterRecordsExcludingLevels({
      records: logData?.records,
      group,
      step,
      searchText,
      commaMode,
      showDebugInfo,
      withAiHintsOnly,
      t,
    });

    if (levels?.length) {
      const levelsSet = new Set(levels);
      result = result.filter(r =>
        levelsSet.has(getNormalizedRichLogLevel(r.level) ?? RichLogLevel.Debug),
      );
    }

    if (sort) {
      result = sortRichLogs({ logs: result, sortType: sort });
    }

    return result;
  }, [
    logData?.records,
    searchText,
    levels,
    group,
    commaMode,
    step,
    sort,
    showDebugInfo,
    withAiHintsOnly,
    t,
  ]);
};
